'use strict';

define('vb/private/stateManagement/router',[
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/stateManagement/router/vbRootRouter',
  'vb/private/stateManagement/router/vbParamRootRouter',
  'vb/private/stateManagement/router/vbPathMarkerRootRouter',
  'vb/private/stateManagement/routerUtils',
  'ojs/ojcontext',
  'vb/private/log', 'vb/private/utils', 'vb/private/constants', 'vb/private/configuration',
  'vb/private/history', 'vb/private/stateManagement/navigationContext', 'urijs/URI',
], (VbRouter, VbRootRouter, VbParamRootRouter, VbPathMarkerRootRouter, RouterUtils,
  ojContext, Log, Utils, Constants, Configuration, History, NavigationContext, URI) => {
  /**
   * @typedef {import('vb/private/stateManagement/container.js').default} Container
   * @typedef {import('vb/private/stateManagement/page.js').default} Page
   * @typedef {VbRootRouter|VbParamRootRouter|VbPathMarkerRootRouter} RootRouter
   */

  /**
   * Error thrown when validating the navigation arguments
   *
   * @type {function}
   */
  const targetError = () => new Error('The target option is only valid with flow');

  /**
   * The object returned by various navigation function
   *
   * @typedef {Object} NavigateResult
   * @property {boolean} navigated
   */

  /**
   * The value of the Router.DID_NAVIGATE property
   * @type {NavigateResult}
   */
  const DID_NAVIGATE = Object.freeze({ navigated: true });

  /**
   * The value of the Router.DID_NOT_NAVIGATE property
   * @type {NavigateResult}
   */
  const DID_NOT_NAVIGATE = Object.freeze({ navigated: false });

  const getBusyContext = () => ojContext.getPageContext().getBusyContext();

  /**
   * A singleton object used to manage tasks related to routing like navigation.
   * It is using a VbRouter object to control the current navigation state of an application.
   */
  class Router {
    constructor() {
      this.log = Log.getLogger('/vb/stateManagement/router');

      // Initialize history with a listener for the history popstate event
      History.init(this.popStateEventListener.bind(this));

      // remove OAuth related data from url without triggering a refresh, e.g., clear the access token fragment
      this.removeOAuthDataFromUrl();

      // A place to store simultaneous navigation during the navigation process
      this.pendingNavigations = {};

      // This property will be initialized in application.load()
      this.application = null;

      // Create the baseUrl property using on the already calculated baseUrl from require config
      this._baseUrl = requirejs.toUrl('');

      this.log.info('Loading application resources from', this.baseUrl);

      // Make sync available on Router
      this.sync = VbRouter.sync;

      /**
       *  @type {RootRouter}
       */
      this.rootRouter = null; // Initialized in init()
    }

    /**
     * One of the 2 objects that can be returned by the QueueNavigate function
     *
     * @type {NavigateResult}
     */
    // eslint-disable-next-line class-methods-use-this
    get DID_NAVIGATE() {
      return DID_NAVIGATE;
    }

    /**
     * One of the 2 objects that can be returned by the QueueNavigate function
     *
     * @type {NavigateResult}
     */
    // eslint-disable-next-line class-methods-use-this
    get DID_NOT_NAVIGATE() {
      return DID_NOT_NAVIGATE;
    }

    /**
     * @type {String}
     */
    get baseUrl() {
      return this._baseUrl;
    }

    /**
     * Initialize the router URL adapter and baseUrl value based on a combination
     * of URL setting and the "useParamAdapter" entry in the app flow definition.
     *
     * @param {string} routerStrategy - The value of the entry in the app flow definition
     * @return {RootRouter}
     */
    init(routerStrategy) {
      if (routerStrategy === Constants.RouterStrategy.QUERY) {
        // Name of the request attribute to be used for the page
        this.rootRouter = new VbParamRootRouter('page');
      } else if (Configuration.appName) {
        this.rootRouter = new VbRootRouter();
      } else {
        // When the router strategy is PATH and app name is not defined then the rootRouter is a vbRouter
        // and vbRootRouter is the parent router, so add the function missing on the VbRouter class.
        // the go function needs to add the URL marker 'vp' to the URL
        this.log.info('Using navigation with URL marker:', Configuration.urlMarker);
        this.rootRouter = new VbPathMarkerRootRouter();
      }

      // Initialize the router baseUrl with the application URL
      // What the JET Router calls the "baseUrl" is the URL of the application in browser
      // eslint-disable-next-line no-param-reassign
      VbRouter.defaults.baseUrl = Configuration.applicationUrl;
      this.log.info('Application URL is', Configuration.applicationUrl);

      // Busy state will be released in the run of the leaf page
      this.setBusyState();

      return this.rootRouter;
    }

    /**
     * A listener of the popstate event used to know when to refresh the input parameters
     * when the navigating back to the same page.
     * @param {{direction: {string}, change: {number}}} details
     */
    popStateEventListener(details) {
      // The rootInstance can be null when traversing history of a deleted switcher element
      // In that case just ignore the entry
      const { rootInstance } = VbRouter;
      if (!rootInstance) {
        return;
      }
      // Set the router busy state on navigation from the browser, the state will be cleared
      // when the page is rendered in page.run()
      this.setBusyState();

      const page = this.getCurrentPage(true);

      if (page && page.fullPath === History.getPagePath()) {
        Promise.resolve().then(() => {
          page.refreshInputParameters();

          // The history state is new, so update page and all parent containers
          // history persisted variables with the values from the browser state
          page.refreshHistoryPersistedVariables();

          return page.invokeAfterNavigateEvent();
        }).finally(() => {
          // Navigation to the same page does not call page.run(), so need to clear the state here
          this.clearBusyState();
        });
      }

      // Call the vbRouter to transition the state of each router in the hierarchy if needed
      rootInstance.handlePopState(details);
    }

    /**
     * Validates and cleans up the navigation options for the navigateAction and navigateToPageAction methods.
     *
     * @param {Container} container - The container instance that owns the navigation.
     * @param {Object} options - The navigation options to validate.
     * @param {String} [options.page] - The path to the destination page. The path is absolute starting
     * at the application or relative to the current page. When used in combination with flow or appPackage,
     * the path cannot be absolute, and it navigates to the page relative to the flow or appPackage.
     * @param {String} [options.flow] - The ID of the destination flow. Change the content of the flow displayed
     * in the current page. When used in combination with a page property, navigates to the page in that flow.
     * @param {String} [options.application] - The ID of the destination App UI. Change which App UI
     * is displayed in the host application. When used in combination with a page property, navigates
     * to the page in that App UI.
     * @param {String} [options.operation] - The navigation operation to perform.
     * @param {String} [options.target] - An optional destination when using the flow property.
     * @param {Object<String, String>} [options.params] - A map of URL parameters. Same as in the navigateToPage
     * action (optional)
     * @param {String} [options.history] - The type of operation on the browser history. Allowed values are 'replace',
     * 'skip' or 'push'. If the value is 'replace', the current browser history entry is replaced, meaning that back
     * button will not go back to it. If the value is 'skip', the URL is left untouched
     * (optional and default is 'push').
     * @returns {Object} The validated and cleaned-up navigation options.
     * @throws {Error} If the navigation options are invalid.
     */
    validateNavigationOptions(container, options) {
      let {
        page, flow, application, target, operation,
      } = options;

      // Note that an empty string means to go to default page
      if (typeof page === 'string') {
        page = page.trim();
      }

      // Note that an empty string means to go to default flow
      if (typeof flow === 'string') {
        flow = flow.trim();

        if (flow.indexOf('/') > 0) {
          throw new Error(`The flow parameter "${flow}" cannot be a path, it should be a flow id.`);
        }
      }

      if (typeof target === 'string') {
        target = target.trim();
      }

      if (operation === Constants.NavigateOperation.PAGE_OLD) {
        if (!page) {
          throw new Error('Cannot navigate to an undefined path.');
        }
      } else if (typeof application === 'string') {
        if (target) {
          throw targetError();
        }

        application = application.trim();
        if (application.length > 0 && !this.application.doesAppUiExist(application)) {
          throw new Error(`The App UI ${application} does not exist.`);
        }

        operation = Constants.NavigateOperation.APP_UI;
      } else if (typeof flow === 'string') {
        operation = Constants.NavigateOperation.FLOW;
      } else if (typeof page === 'string') {
        if (target) {
          throw targetError();
        }
        operation = Constants.NavigateOperation.PAGE;
      } else {
        throw new Error('Cannot navigate to an undefined location.');
      }

      const validOptions = {
        page,
        flow,
        application,
        target,
        operation,
        params: options.params,
        history: options.history,
      };

      // process additional validation specific to container; throws error if validation fails
      container.validateNavigation(validOptions);

      // if we are here all validations passed.
      return validOptions;
    }

    /**
     * Manage page navigation such that when an attempt to navigate is made while navigating,
     * the current navigation is cancelled and the new one is executed. The goal being that
     * when multiple navigation are queued, only the last navigation should complete.
     *
     * @param {Container} container - The container on which the navigation is performed.
     * @param {Object} opts - The set of options for the navigation.
     * @param {String} [opts.page] - The path to the destination page. The path is absolute starting
     * at the application or relative to the current page. When used in combination with flow or appPackage,
     * the path cannot be absolute, and it navigates to the page relative to the flow or appPackage.
     * @param {String} [opts.flow] - The ID of the destination flow. Change the content of the flow displayed
     * in the current page. When used in combination with a page property, navigates to the page in that flow.
     * @param {String} [opts.application] - The ID of the destination App UI. Change which App UI
     * is displayed in the host application. When used in combination with a page property, navigates
     * to the page in that App UI.
     * @param {String} [opts.operation] - The navigation operation to perform.
     * @param {'self'|'parent'} [opts.target="self"] - The target of the destination for flow navigation.
     * This is only valid with the flow to change the content of the parent flow instead of the nested flow.
     * @param {Object} [opts.params] - A map of URL parameters. Same as in the navigateToPage
     * action.
     * @param {'push'|'replace'|'skip'} [opts.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @returns {Promise<NavigateResult>} A promise that resolves to an object with a property named 'navigated'
     * which value is true if a navigation occurred.
     */
    queueNavigate(container, opts) {
      return Promise.resolve()
        .then(() => this.validateNavigationOptions(container, opts))
        .then((options) => container.validateNavigationInDt(options)
          .then((canNavigate) => {
            if (!canNavigate) {
              return DID_NOT_NAVIGATE;
            }

            // If a navigation to the same page already exist, return the existing promise
            // instead of creating a new one.
            let navContext = this.getPendingNavigation(container, options);
            if (navContext) {
              return navContext.navigatePromise;
            }

            // We're about to start a new navigation, so start cancelling all existing navigation
            this.cancelPendingNavigations();

            // Create a new navigation context and add it to the list of pending navigation.
            navContext = this.addPendingNavigation(container, options);

            // add a busy state just to be safe and consistent with navigateBack which will be cleared after
            // page enter
            this.setBusyState();

            return navContext.navigate()
              .then((result) => {
                // When navigated is true, the busy state should not be cleared until the page is done
                // (after vbEnter), but if navigated is false, clearBusyState right away
                if (result && result.navigated === false) {
                  this.clearBusyState();
                }

                return result;
              })
              .catch((error) => {
                this.clearBusyState();
                throw error;
              })
              .finally(() => {
                this.deletePendingNavigation(navContext);
              });
          }))
        .catch((error) => {
          // Log the error because the action just return a failure outcome without errors
          this.log.error(error);
          throw error;
        });
    }

    /**
     * Create a new navigate context and add it to the pending navigations
     * @param {Container} container  the container driving the navigation
     * @param {Object} options       a set of navigation options
     * @returns {NavigationContext}  the newly created navigation context
     */
    addPendingNavigation(container, options) {
      const navContext = new NavigationContext(container, options);
      this.pendingNavigations[navContext.id] = navContext;
      return navContext;
    }

    /**
     * Deletes a navigation context from the pending navigations.
     *
     * @param {NavigationContext} navContext - The navigation context to be deleted.
     * @throws {TypeError} If navContext is not an instance of NavigationContext.
     */
    deletePendingNavigation(navContext) {
      if (navContext.isCancelled()) {
        this.log.info('Ending cancelled navigation to:', navContext.options);
      } else {
        this.log.info('Ending executed navigation to:', navContext.options);
      }

      delete this.pendingNavigations[navContext.id];
    }

    /**
     * Return a navigationContext from the pending navigation that matches
     * the navigation requirement (container + options)
     * @param {Container} container  the container driving the navigation
     * @param {Object} options       the navigation options
     * @return {NavigationContext}   the navigation context in the pending navigations
     * matching container and options
     */
    getPendingNavigation(container, options) {
      const values = Object.values(this.pendingNavigations);

      for (let index = 0; index < values.length; index += 1) {
        const item = values[index];
        // Check if the navigation is the same
        if (item.equals(container, options)) {
          return item;
        }
      }

      return null;
    }

    /**
     * Cancels all active pending navigation
     */
    cancelPendingNavigations() {
      Object.values(this.pendingNavigations).forEach((item) => {
        // if we are in the middle of a navigation, cancel it
        if (item.isActive()) {
          item.cancel();
        }
      });
    }

    /**
     * Navigate to an external site with given a set of input parameters.
     *
     * @param {Object} options - Options for navigating to an external site.
     * @param {String} options.url - The URL to navigate to (required).
     * @param {Object<String, String>} [options.params] - A map of URL parameters (optional).
     * @param {String} [options.hash] - The hash entry (optional).
     * @param {String} [options.history=Constants.HistoryMode.PUSH] - Effect on the browser history.
     * Allowed values are 'replace' or 'push'. If the value is 'replace', the current browser history entry is replaced,
     * meaning that back button will not go back to it (optional and default is 'push').
     * @returns {Promise<void>} A promise that resolves when the navigation is complete.
     */
    navigateToExternal(options) {
      const href = Utils.resolveIfObservable(options.url);

      if (!href) {
        throw new Error('The property url is a required parameter for navigateToExternal action.');
      }
      // For DT environment, retrieve current and destination page then invoke the
      // canNavigatePage to give a chance to DT to cancel navigation.
      const currentPath = this.application.getCurrentPagePath();
      return this.application.runtimeEnvironment.canNavigateToUrl(currentPath, href).then((canNavigate) => {
        if (canNavigate) {
          const uri = new URI(href);

          if (options.params) {
            uri.addSearch(options.params);
          }

          if (options.hash) {
            uri.hash(options.hash);
          }

          if (options.history === Constants.HistoryMode.REPLACE) {
            window.location.replace(uri.href());
          } else {
            window.location.assign(uri.href());
          }
        }
      });
    }

    /**
     * Implementation of the openUrl action.
     * Open the specified resource in the current window or in a new window using the window.open() API
     * as defined at {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/open|Window.open()}
     *
     * @param {Object} options
     * @param {String} options.url The url to navigate to
     * @param {Object} [options.params] An object with each property is a URL parameters
     * @param {String} [options.hash] The hash entry
     * @param {String} [options.history] Effect on the browser history
     * This is only used when opening the resource in the same window.
     * Allowed values are 'replace' or 'push'. Default is 'push'.
     * If the value is 'replace', the current browser history entry is
     * replaced instead of pushed, meaning that the browser back button
     * will not go back to it.
     * @param {String} [options.windowName] A string identifying the window as defined in window.open API
     * If not defined, the URL will be open in the current window otherwise
     * refer to the window.open API.
     * @returns {Promise<void>}
     */
    openUrl(options) {
      const url = Utils.resolveIfObservable(options.url);

      // Empty string is valid but not undefined or null
      if (url === undefined || url === null) {
        return Promise.reject(new Error('The property "url" is required for openUrl.'));
      }

      const windowName = options.windowName || '_self';
      if (windowName.indexOf(' ') >= 0) {
        return Promise.reject(new Error('The property "windowName" should not contain whitespace.'));
      }

      // For DT environment, retrieve current path then invoke the canOpenUrl to give a chance
      // to DT to cancel the operation.
      return this.application.runtimeEnvironment.canOpenUrl(this.application.getCurrentPagePath(),
        url, options.windowName)
        .then((result) => {
          if (result) {
            const uri = new URI(url);

            if (options.params) {
              uri.addSearch(options.params);
            }

            if (options.hash) {
              uri.hash(options.hash);
            }

            const href = uri.href();

            if (windowName === '_self') {
              // When operating on the same window in a web app, use replace or assign depending on the history setting
              if (options.history === Constants.HistoryMode.REPLACE) {
                window.location.replace(href);
              } else {
                window.location.assign(href);
              }
            } else {
              window.open(href, windowName);
            }
          }
        });
    }

    /**
     * Navigate back to the previous page in browser history with input parameters.
     *
     * @param {Page} page - The current page instance.
     * @param {Object} [options] - The set of options for the navigateBack action.
     * @param {Object} [options.params] - The input parameters
     * @returns {Promise<void>}
     */
    navigateBack(page, options) {
      return Promise.resolve().then(() => {
        // Since runtimeEnvironment.canNavigateBack and history.back are both async, there is a window
        // where the application could be in a non-busy state during page transition. We close that
        // window here by setting a busy state which will be cleared after page enter (page.js run method)
        this.setBusyState();

        // For DT environment, invoke the canNavigatePage with the current page to give a
        // chance to DT to cancel navigation.
        return this.application.runtimeEnvironment.canNavigateBack(page.fullPath).then((canNavigate) => {
          if (canNavigate) {
            // Call navigateBack on rootInstance to satisfy the different behavior for switcher
            return VbRouter.rootInstance.navigateBack(page, options.params)
              .catch((error) => {
                this.clearBusyState();
                throw error;
              });
          }

          this.clearBusyState();
          return undefined;
        });
      });
    }

    /**
     * Traverse the router hierarchy to retrieve the current page.
     * The content of the application is made of nested flows and the leaf flow is
     * displaying a page. This routine traverse the hierarchy of flows using the
     * router hierarchy and return the page on the leaf flow.
     *
     * When the full argument is truthy, the function retrieve the current page of
     * the switcher.
     *
     * @param  {boolean} [full] when true, traverse the full container hierarchy to the switcher current page
     * @return {Page}   the current page
     */
    getCurrentPage(full = false) {
      return RouterUtils.getCurrentPage(this.rootRouter, full);
    }

    /**
     * Return the container instance for a path. Instance can either be a flow or a page.
     * @param  {Container} container the container where the path will be searched
     * @param  {String} path         the path to search
     * @return {Container}           Either a flow or a page
     */
    getContainer(container, path) {
      const index = path.indexOf('/');

      if (index > 0) {
        const id = path.substring(0, index);
        return this.getContainer(container.getContainer(id), path.substr(index + 1));
      }

      return container.getContainer(path);
    }

    /**
     * Calculates the URL of a page or flow given its ID path.
     * This is mainly needed to build the return path when calling the login service.
     * It does not prepend the base URL. When this method is called while loading the
     * application, all existing input parameters are copied to the returned path, this
     * is to preserve the bookmarked data when calling the login service.
     *
     * @param {String} [path=''] - The ID path of the page or flow (e.g., "/pageId/flowId/pageId").
     * @param {String} [routerStrategy] - The router strategy to use ('path' or 'query').
     * Defaults to the application's default router strategy if not provided.
     * @returns {String} The URL to this page or flow.
     */
    getUrlFromPath(path = '', routerStrategy) {
      // Paths are always absolute. In case the path starts with '/', remove it
      if (path[0] === '/') {
        path = path.substring(1);
      }

      let uri;
      if (this.application.started) {
        uri = new URI(''); // Create a blank uri
      } else {
        // When loading the application for the first time, we need to copy the
        // existing request parameters to the return URL
        uri = new URI();
      }

      if (routerStrategy ? routerStrategy === Constants.RouterStrategy.QUERY : this.application.isQueryStrategy()) {
        if (path) {
          const segments = path.split('/');
          let key = VbRouter.defaults.rootInstanceName;

          segments.forEach((segment) => {
            uri.addSearch(key, segment);
            key = segment;
          });
        }
        return uri.search();
      }

      if (Configuration.appName) {
        return `${path}${uri.search()}`;
      }

      return `${Configuration.urlMarker}/${path}${uri.search()}`;
    }

    /**
     * Parse the given # into a map of keys and values.
     *
     * @param hash the hash to parse
     * @returns {Object}
     */
    // eslint-disable-next-line class-methods-use-this
    parseFragment(hash) {
      const uri = new URI(hash);
      return URI.parseQuery(uri.fragment());
    }

    /**
     * Remove fragment from the current URL if the fragment is an access token or code query parameter in case
     * of an authorization code. This is done without triggering a refresh.
     */
    // eslint-disable-next-line class-methods-use-this
    removeOAuthDataFromUrl() {
      const { location } = window;
      const fragment = location.hash;

      // Only remove the hash when the fragment is an access token
      if (fragment && fragment.indexOf('token_type') > 0 || fragment.indexOf('access_token') > 0) {
        window.history.replaceState(window.history.state, '', location.pathname + location.search);
        this.accessTokenHash = fragment;
      } else {
        // remove ?code from the queries
        const uri = new URI(location.href);
        const queries = uri.search(true);

        if (queries.code) {
          this.authCode = queries.code;
          // Preserve the OAuth state with the authorization code so the security provider can
          // validate the returned state before exchanging the code.
          this.authState = queries.state;

          uri.search((data) => {
            // eslint-disable-next-line no-param-reassign
            delete data.code;
            // eslint-disable-next-line no-param-reassign
            delete data.state;
          });

          window.history.replaceState(window.history.state, '', uri.href());
        }
      }
    }

    /**
     * Gets the url segment after the application name.
     *
     * @return  {string}  The url segment after the application name.
     */
    // eslint-disable-next-line class-methods-use-this
    getUrlSegmentAfterAppName() {
      let urlId;

      if (Configuration.appName) {
        const path = window.location.pathname;

        // Extract the segment immediately following the appName
        urlId = path.substring(path.indexOf(Configuration.appName) + Configuration.appName.length + 1);
        const slash = urlId.indexOf('/');
        if (slash >= 0) {
          urlId = urlId.substring(0, slash);
        }
      }

      return urlId;
    }

    /**
     * Add a busy state to the page's busy context during navigation.
     *
     * Note: For now, to keep it simple, we will assume there can be only one busy state outstanding meaning
     * no navigation can happen in the middle of another navigation. We will address that use case in the
     * future.
     */
    setBusyState() {
      if (!this.busyStateResolver) {
        this.busyStateResolver = getBusyContext().addBusyState({ description: 'VB Router' });
      }
    }

    /**
     * Clear the outstanding busy state.
     */
    clearBusyState() {
      if (this.busyStateResolver) {
        this.busyStateResolver();
        delete this.busyStateResolver;
      }
    }

    /**
     * Return the authorization code and delete it, so it doesn't get reused.
     */
    consumeAuthCode() {
      const authCode = this.authCode;

      if (authCode) {
        delete this.authCode;
      }

      return authCode;
    }

    /**
     * Return the authorization state and delete it, so it doesn't get reused.
     *
     * @returns {String|undefined} The authorization state captured from the URL, if any.
     */
    consumeAuthState() {
      const authState = this.authState;

      delete this.authState;

      return authState;
    }

    /**
     * Implements clearBusyContextBeforeDialog() in vb/helpers/Navigate.js
     * It clears the existing busy context and return a function to restore the busyContext.
     */
    clearBusyContextBeforeDialog() {
      // For the test to be able to stop on the dialog, all busy contexts are cleared.
      this.clearBusyState();
      getBusyContext().clear();
      return () => {
        this.setBusyState();
      };
    }
  }

  // Return a singleton object representing the router for this application.
  return new Router();
});

